import * as React from 'react';
import { useEffect, useState } from 'react';
import { TorchPresenceEvent } from '../../../hooks/useTorchPresence.js';
import style from './TorchPresence.module.css';

export interface TorchPresenceProps {
  /** Live event stream from useTorchPresence. New entries trigger 1.5s pulse messages. */
  events: TorchPresenceEvent[];
  /** Live world counter from useTorchCounter. */
  worldCount: number;
}

/**
 * Always-visible read-only counter at the top of the torch chrome, plus a
 * 1.5s pulse line that appears when another bearer lights theirs (driven by
 * the events array from useTorchPresence).
 *
 * Hardcoded English strings here are wired through Tolgee in Phase 12.
 */
export function TorchPresence({ events, worldCount }: TorchPresenceProps) {
  // Track which event timestamps are currently animating so we render one
  // pulse line per fresh event. After the animation duration we drop the
  // entry from the visible set; the underlying event stays in `events` for
  // dedupe but won't render a second pulse.
  const [activePulses, setActivePulses] = useState<TorchPresenceEvent[]>([]);

  useEffect(() => {
    if (events.length === 0) return;
    const latest = events[events.length - 1];
    if (latest.type !== 'torch:lit') return;
    setActivePulses((prev) => [...prev, latest]);
    const timer = window.setTimeout(() => {
      setActivePulses((prev) =>
        prev.filter((event) => event.timestamp !== latest.timestamp)
      );
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [events]);

  const noun = worldCount === 1 ? 'Peace Torch' : 'Peace Torches';

  return (
    <div className={style.strip} role="status" aria-live="polite">
      <p className={style.counter}>
        🔥{' '}
        <span className={style.counterValue}>
          {worldCount.toLocaleString()}
        </span>{' '}
        {noun} lit right now
      </p>
      {activePulses.map((event) => (
        <p key={event.timestamp} className={style.event}>
          Another bearer just lit theirs
        </p>
      ))}
    </div>
  );
}
